import React, { useState, useEffect } from "react";
import Field from "./Field";
import useStyles from "./styles";
import styles from "./adminLogin.module.css";

import { useDispatch } from "react-redux";
import { adminSignup } from "../../actions/auth";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  ListItemText,
  List,
  ListItem,
  Paper,
  Divider,
  FormHelperText,
} from "@material-ui/core";

import { Paper as MuiPaper, Grid, Container } from "@material-ui/core";
import { useSnackbar } from "react-simple-snackbar";
import CircularProgress from "@material-ui/core/CircularProgress";
import { fetchClientsSearch } from "../../api";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  profilePicture: "",
  bio: "",
  userType: "",
  selectedClients: [],
};

const AdminLogin = () => {
  const classes = useStyles();
  const [formData, setFormData] = useState(initialState);
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar] = useSnackbar({
    position: "bottom-right",
    style: {
      backgroundColor: "#333",
      color: "#fff",
      fontSize: "16px",
      textAlign: "center",
    },
  });
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [clientsPage, setClientsPage] = useState(0);
  const [clientsHasMore, setClientsHasMore] = useState(true);

  // Fetch clients when dealer type is selected
  useEffect(() => {
    if (formData.userType === "dealer") {
      setClientsLoading(true);
      setClientsPage(0);
      setClientsHasMore(true);
    } else {
      // Reset clients when not dealer
      setClients([]);
      setSelectedClients([]);
      setFormData((prev) => ({ ...prev, selectedClients: [] }));
      setSearchQuery("");
      setDebouncedSearchQuery("");
      setClientsPage(0);
      setClientsHasMore(false);
    }
  }, [formData.userType]);

  const handleShowPassword = () => setShowPassword(!showPassword);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear errors when user starts typing
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    }

    // Reset client selection when user type changes
    if (e.target.name === "userType") {
      setSelectedClients([]);
      setFormData((prev) => ({ ...prev, selectedClients: [] }));
      setSearchQuery("");
      setDebouncedSearchQuery("");

      // Set loading state immediately for dealer selection
      if (e.target.value === "dealer") {
        setClientsLoading(true);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setClientsPage(0);
  };

  // Debounce search query to prevent lag during typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    if (formData.userType !== "dealer") return;
    setClients([]);
    setClientsPage(0);
    setClientsHasMore(!debouncedSearchQuery);
  }, [debouncedSearchQuery, formData.userType]);

  useEffect(() => {
    if (formData.userType !== "dealer") return;

    let isCancelled = false;
    const fetchClients = async () => {
      setClientsLoading(true);
      try {
        const response = await fetchClientsSearch({
          query: debouncedSearchQuery,
          page: clientsPage,
          limit: debouncedSearchQuery ? 0 : 20,
        });
        const clientsData = response?.data?.data || [];
        const totalItems = response?.data?.totalItems || 0;
        if (isCancelled) return;

        setClients((prev) =>
          clientsPage === 0 ? clientsData : [...prev, ...clientsData]
        );
        setClientsHasMore(
          debouncedSearchQuery ? false : (clientsPage + 1) * 20 < totalItems
        );
      } catch (error) {
        console.error("Error fetching clients:", error);
        if (!isCancelled) {
          setClients([]);
          setClientsHasMore(false);
        }
      } finally {
        if (!isCancelled) {
          setClientsLoading(false);
        }
      }
    };

    fetchClients();

    return () => {
      isCancelled = true;
    };
  }, [formData.userType, debouncedSearchQuery, clientsPage]);

  const handleClientListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 40;
    if (isNearBottom && clientsHasMore && !clientsLoading) {
      setClientsPage((prev) => prev + 1);
    }
  };

  const handleClientToggle = React.useCallback(
    (client) => {
      // Single client selection - replace any existing selection
      setSelectedClients([client]);

      // Auto-fill basic information from selected client
      const phoneNumber = client.phone ? client.phone.replace(/^\+91/, "") : "";

      // Update form data with client selection and auto-filled information
      setFormData((prevFormData) => ({
        ...prevFormData,
        firstName: client.contactPerson || "",
        lastName: "", // Leave last name blank as requested
        email: phoneNumber, // Use phone number as email field
        selectedClients: [client._id],
      }));

      // Clear client selection error
      if (errors.selectedClients) {
        setErrors((prev) => ({ ...prev, selectedClients: "" }));
      }
    },
    [errors.selectedClients]
  );

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Basic validation
    if (!formData.firstName.trim()) {
      newErrors.firstName =
        formData.userType === "dealer"
          ? "Contact person is required"
          : "First name is required";
      isValid = false;
    }

    // Only require last name for non-dealer users
    if (formData.userType !== "dealer" && !formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email or mobile number is required";
      isValid = false;
    } else {
      // Validate email or mobile number format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number format
      const trimmedEmail = formData.email.trim();

      if (!emailRegex.test(trimmedEmail) && !mobileRegex.test(trimmedEmail)) {
        newErrors.email =
          "Please enter a valid email address or 10-digit mobile number";
        isValid = false;
      }
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }
    if (!formData.userType) {
      newErrors.userType = "User type is required";
      isValid = false;
    }

    // Dealer client selection validation
    if (
      formData.userType === "dealer" &&
      formData.selectedClients.length !== 1
    ) {
      newErrors.selectedClients =
        "Please select exactly one client for the dealer";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    dispatch(adminSignup(formData, openSnackbar, setLoading));
    setLoading(true);
  };

  // const googleSuccess = async (res) => {
  //     const result = jwtDecode(res.credential);
  //     const token = res?.credential;
  //     dispatch(createProfile({name: result?.name, email: result?.email, userId: result?.jti, phoneNumber: '', businessName: '', contactAddress: '', logo: result?.picture, website: ''}))

  //     try {
  //         dispatch({ type: "AUTH", data: {result, token}})

  //         window.location.href='/adminDashboard'

  //     } catch (error) {
  //         console.log(error)
  //     }
  // }
  // const googleError =(error) => {
  //     console.log(error)
  //     console.log("Google Sign In was unseccassful. Try again later")
  // }

  // if (user) {
  //   history.push("/adminDashboard");
  // }

  return (
    <>
      <Container
        component="main"
        maxWidth="md"
        style={{ paddingTop: "20px", paddingBottom: "20px" }}
      >
        <MuiPaper
          elevation={2}
          style={{
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "0px",
          }}
        >
          <form
            className={classes.form}
            onSubmit={handleSubmit}
            style={{ padding: "24px" }}
          >
            <Grid container spacing={2}>
              {/* User Type Selection */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  style={{
                    color: "#333",
                    marginBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  User Type
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.userType}
                  variant="outlined"
                  style={{ marginTop: "8px" }}
                >
                  <InputLabel
                    id="userType-label"
                    style={{ backgroundColor: "white", padding: "0 8px" }}
                  >
                    User Type
                  </InputLabel>
                  <Select
                    labelId="userType-label"
                    id="userType"
                    name="userType"
                    value={formData.userType}
                    onChange={handleChange}
                    variant="outlined"
                  >
                    <MenuItem value="user">Salesperson</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    {/* <MenuItem value="dealer">Dealer</MenuItem> */}
                  </Select>
                  {errors.userType && (
                    <FormHelperText>{errors.userType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Client Selection for Dealer */}
              {formData.userType === "dealer" && (
                <Grid item xs={12}>
                  <Box
                    style={{
                      marginTop: "16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "16px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      style={{
                        color: "#333",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Client Selection
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: "#666", marginBottom: "16px" }}
                    >
                      Select the client this dealer will be associated with.
                      Basic information will be auto-filled from the selected
                      client.
                    </Typography>

                    <>
                      {/* Search Field */}
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search clients by name, phone, or address..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        style={{ marginBottom: "12px" }}
                        size="small"
                        InputProps={{
                          style: { fontSize: "14px" },
                        }}
                      />

                      {clientsLoading && clients.length === 0 && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          p={2}
                        >
                          <CircularProgress
                            size={16}
                            style={{ color: "#ef7f1a" }}
                          />
                          <Typography
                            variant="body2"
                            style={{
                              marginLeft: 8,
                              color: "#666",
                              fontSize: "12px",
                            }}
                          >
                            Loading clients...
                          </Typography>
                        </Box>
                      )}

                      {/* Client List */}
                      <Paper
                        style={{
                          maxHeight: "250px",
                          overflow: "auto",
                          border: "1px solid #e0e0e0",
                          backgroundColor: "white",
                        }}
                        onScroll={handleClientListScroll}
                      >
                        {clients.length === 0 ? (
                          <Box p={2} textAlign="center">
                            <Typography
                              variant="body2"
                              style={{ color: "#999" }}
                            >
                              {searchQuery
                                ? "No clients found matching your search"
                                : "No clients available"}
                            </Typography>
                          </Box>
                        ) : (
                          <List dense>
                            {clients.map((client) => {
                              const isSelected = selectedClients.some(
                                (selected) => selected._id === client._id
                              );
                              return (
                                <ListItem
                                  key={client._id}
                                  button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleClientToggle(client);
                                  }}
                                  style={{
                                    backgroundColor: isSelected
                                      ? "#e3f2fd"
                                      : "transparent",
                                    borderBottom: "1px solid #f5f5f5",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    minHeight: "48px",
                                    borderRadius: "4px",
                                    margin: "2px 0",
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        style={{
                                          fontWeight: "500",
                                          fontSize: "14px",
                                        }}
                                      >
                                        {client.name}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography
                                        variant="caption"
                                        style={{
                                          color: "#666",
                                          fontSize: "12px",
                                        }}
                                      >
                                        {client.phone} � {client.address}
                                      </Typography>
                                    }
                                  />
                                  {isSelected && (
                                    <Typography
                                      variant="caption"
                                      style={{
                                        color: "#1976d2",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Selected
                                    </Typography>
                                  )}
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                      </Paper>
                      {clientsLoading && clients.length > 0 && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          p={1}
                        >
                          <CircularProgress
                            size={14}
                            style={{ color: "#ef7f1a" }}
                          />
                          <Typography
                            variant="caption"
                            style={{ marginLeft: 6, color: "#666" }}
                          >
                            Loading more...
                          </Typography>
                        </Box>
                      )}
                    </>

                    {errors.selectedClients && (
                      <Typography
                        variant="body2"
                        style={{
                          color: "#f44336",
                          marginTop: "8px",
                          fontSize: "12px",
                        }}
                      >
                        {errors.selectedClients}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  style={{
                    color: "#333",
                    marginBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  Basic Information
                </Typography>
              </Grid>

              {formData.userType === "dealer" ? (
                <Grid item xs={12}>
                  <Field
                    name="firstName"
                    label="Contact Person"
                    handleChange={handleChange}
                    required
                    autoFocus
                    error={errors.firstName}
                    value={formData.firstName}
                    placeholder={
                      selectedClients.length > 0
                        ? "Auto-filled from selected client"
                        : ""
                    }
                  />
                </Grid>
              ) : (
                <Grid item xs={12} sm={6}>
                  <Field
                    name="firstName"
                    label="First Name"
                    handleChange={handleChange}
                    required
                    autoFocus
                    error={errors.firstName}
                    value={formData.firstName}
                    placeholder={
                      selectedClients.length > 0
                        ? "Auto-filled from selected client"
                        : ""
                    }
                  />
                </Grid>
              )}

              {formData.userType !== "dealer" && (
                <Grid item xs={12} sm={6}>
                  <Field
                    name="lastName"
                    label="Last Name"
                    handleChange={handleChange}
                    error={errors.lastName}
                    value={formData.lastName}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Field
                  name="email"
                  label="Email Address or Mobile Number"
                  handleChange={handleChange}
                  required
                  type="text"
                  error={errors.email}
                  value={formData.email}
                  placeholder={
                    selectedClients.length > 0
                      ? "Auto-filled from selected client"
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Field
                  name="password"
                  label="Password"
                  handleChange={handleChange}
                  required
                  type={showPassword ? "text" : "password"}
                  handleShowPassword={handleShowPassword}
                  error={errors.password}
                  value={formData.password}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Field
                  name="confirmPassword"
                  required
                  label="Confirm Password"
                  handleChange={handleChange}
                  type="password"
                  error={errors.confirmPassword}
                  value={formData.confirmPassword}
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box style={{ marginTop: "24px", textAlign: "center" }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress size={20} style={{ color: "#ef7f1a" }} />
                  <Typography
                    variant="body2"
                    style={{ marginLeft: 12, color: "#666" }}
                  >
                    Creating user...
                  </Typography>
                </Box>
              ) : (
                <button
                  className={styles.loginBtn}
                  style={{
                    backgroundColor: "#ef7f1a",
                    border: "none",
                    borderRadius: "6px",
                    padding: "10px 24px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "white",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "all 0.2s ease",
                    minWidth: "120px",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#d66a00";
                    e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#ef7f1a";
                    e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                  }}
                >
                  Create User
                </button>
              )}
            </Box>
          </form>
        </MuiPaper>
      </Container>
    </>
  );
};

export default AdminLogin;